import { BrowserRouter, Route, Routes } from "react-router-dom";
import { UserLogin, Index, HomePage } from "./MainPages/Collection";
import { ProtectedRoute } from "./Components/Collect";
import { AuthRoute, PasswordUpdate, UserProfile } from "./Components/Collect";
import { UserContextProvider } from "./Context/UsersContext";
import { DataProvider } from "./Context/DAtaContext";
import { ToastContainer } from "react-toastify";
import { FarmerHome } from "./Farmer/collect";
import VANOHome from "./VANO/VANOHome";
import HomePackage from "./Package/HomePackage";
import Objective from "./Package/Objective";
import PaymentsUpdater from "./Payments/PaymentsUpdater";
import PasswordReset from "./MainPages/Passwordreset";
import OfflineFarmers from "./MainPages/OfflineFarmers";
import FarmerSettings from "./MainPages/FarmerSettings";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Sparkles, CheckCircle2, Loader } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import Statistics from "./MainPages/Statistics";
import "react-activity/dist/library.css";
import "react-toastify/dist/ReactToastify.css";
import "react-phone-input-2/lib/style.css";

function useServiceWorkerUpdate() {
  const [waitingWorker, setWaitingWorker] = useState(null);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    const handleUpdateAvailable = (event) => {
      const registration = event.detail;

      if (registration?.waiting) {
        setWaitingWorker(registration.waiting);
      }
    };

    window.addEventListener("swUpdateAvailable", handleUpdateAvailable);

    // Check if a worker was already waiting before React mounted
    navigator.serviceWorker.ready.then((registration) => {
      if (registration.waiting) {
        setWaitingWorker(registration.waiting);
      }
    });

    return () => {
      window.removeEventListener("swUpdateAvailable", handleUpdateAvailable);
    };
  }, []);

  const applyUpdate = () => {
    if (!waitingWorker) return;

    waitingWorker.postMessage({
      type: "SKIP_WAITING",
    });
  };

  return {
    updateAvailable: !!waitingWorker,
    applyUpdate,
  };
}

/*
 * ---------------------------------------------------------
 * STORAGE KEYS
 * ---------------------------------------------------------
 */

const UPDATE_IN_PROGRESS_KEY = "app-update-in-progress";
const UPDATE_STARTED_AT_KEY = "app-update-started-at";

/*
 * Prevent a stale update flag from remaining forever.
 */
const UPDATE_STATE_MAX_AGE = 5 * 60 * 1000;

/*
 * ---------------------------------------------------------
 * UPDATE BANNER
 * ---------------------------------------------------------
 */

function UpdateBanner() {
  const { updateAvailable, applyUpdate } = useServiceWorkerUpdate();

  const reduceMotion = useReducedMotion();

  /*
   * -------------------------------------------------------
   * UI STATE
   * -------------------------------------------------------
   */

  const [isUpdating, setIsUpdating] = useState(false);

  const [updateError, setUpdateError] = useState("");

  const [showSuccess, setShowSuccess] = useState(false);

  /*
   * -------------------------------------------------------
   * REFS
   * -------------------------------------------------------
   */

  /*
   * One AudioContext for the lifetime
   * of this component.
   */
  const audioContextRef = useRef(null);

  /*
   * Prevent sounds from playing repeatedly.
   */
  const updateSoundPlayedRef = useRef(false);
  const successSoundPlayedRef = useRef(false);

  /*
   * If browser autoplay rules block a sound,
   * remember that it should be retried after
   * the next user interaction.
   */
  const pendingUpdateSoundRef = useRef(false);
  const pendingSuccessSoundRef = useRef(false);

  /*
   * Keep latest updateAvailable value available
   * inside callbacks and delayed checks.
   */
  const updateAvailableRef = useRef(updateAvailable);

  useEffect(() => {
    updateAvailableRef.current = updateAvailable;
  }, [updateAvailable]);

  /*
   * -------------------------------------------------------
   * STORAGE HELPERS
   * -------------------------------------------------------
   */

  const clearStoredUpdateState = useCallback(() => {
    if (typeof window === "undefined") return;

    sessionStorage.removeItem(UPDATE_IN_PROGRESS_KEY);

    sessionStorage.removeItem(UPDATE_STARTED_AT_KEY);
  }, []);

  /*
   * -------------------------------------------------------
   * GET / CREATE AUDIO CONTEXT
   * -------------------------------------------------------
   */

  const getAudioContext = useCallback(() => {
    if (typeof window === "undefined") {
      return null;
    }

    /*
     * If a valid context already exists,
     * reuse it.
     */
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      return audioContextRef.current;
    }

    const AudioContextClass = window.AudioContext || window.webkitAudioContext;

    if (!AudioContextClass) {
      console.warn("Web Audio API is not supported by this browser.");

      return null;
    }

    try {
      audioContextRef.current = new AudioContextClass();

      return audioContextRef.current;
    } catch (error) {
      console.warn("Unable to create AudioContext:", error);

      return null;
    }
  }, []);

  /*
   * -------------------------------------------------------
   * RESUME AUDIO
   * -------------------------------------------------------
   */

  const resumeAudio = useCallback(async () => {
    const ctx = getAudioContext();

    if (!ctx) {
      return false;
    }

    try {
      if (ctx.state !== "running") {
        await ctx.resume();
      }

      return ctx.state === "running";
    } catch (error) {
      /*
       * This is expected on browsers that
       * require direct user interaction.
       */
      console.warn("AudioContext could not be resumed:", error);

      return false;
    }
  }, [getAudioContext]);

  /*
   * -------------------------------------------------------
   * PLAY ONE TONE
   * -------------------------------------------------------
   */

  const playTone = useCallback(
    async ({
      frequency,
      startDelay = 0,
      duration = 0.18,
      volume = 0.12,
      type = "sine",
    }) => {
      const ctx = getAudioContext();

      if (!ctx) {
        return false;
      }

      const isReady = await resumeAudio();

      if (!isReady) {
        return false;
      }

      try {
        const startTime = ctx.currentTime + startDelay;

        const oscillator = ctx.createOscillator();

        const gainNode = ctx.createGain();

        oscillator.type = type;

        oscillator.frequency.setValueAtTime(frequency, startTime);

        /*
         * Start almost silent.
         */
        gainNode.gain.setValueAtTime(0.0001, startTime);

        /*
         * Gentle fade in.
         */
        gainNode.gain.exponentialRampToValueAtTime(volume, startTime + 0.02);

        /*
         * Fade out.
         */
        gainNode.gain.exponentialRampToValueAtTime(
          0.0001,
          startTime + duration,
        );

        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);

        /*
         * Disconnect nodes after sound ends
         * to release resources.
         */
        oscillator.onended = () => {
          try {
            oscillator.disconnect();
            gainNode.disconnect();
          } catch {
            // Nodes may already be disconnected.
          }
        };

        oscillator.start(startTime);

        oscillator.stop(startTime + duration + 0.02);

        return true;
      } catch (error) {
        console.warn("Unable to play notification tone:", error);

        return false;
      }
    },
    [getAudioContext, resumeAudio],
  );

  /*
   * -------------------------------------------------------
   * UPDATE AVAILABLE SOUND
   * -------------------------------------------------------
   */

  const playUpdateAvailableSound = useCallback(async () => {
    /*
     * Already successfully played.
     */
    if (updateSoundPlayedRef.current) {
      return true;
    }

    /*
     * Friendly ding-dong.
     */
    const firstTonePlayed = await playTone({
      frequency: 880,
      duration: 0.14,
      volume: 0.12,
    });

    /*
     * Browser probably blocked audio.
     *
     * Do NOT mark the sound as already played.
     */
    if (!firstTonePlayed) {
      pendingUpdateSoundRef.current = true;

      return false;
    }

    await playTone({
      frequency: 1174.66,
      startDelay: 0.12,
      duration: 0.22,
      volume: 0.14,
    });

    /*
     * Only mark it as played AFTER
     * audio was actually allowed.
     */
    updateSoundPlayedRef.current = true;

    pendingUpdateSoundRef.current = false;

    return true;
  }, [playTone]);

  /*
   * -------------------------------------------------------
   * UPDATE SUCCESS SOUND
   * -------------------------------------------------------
   */

  const playUpdateSuccessSound = useCallback(async () => {
    if (successSoundPlayedRef.current) {
      return true;
    }

    /*
     * C5
     */
    const firstTonePlayed = await playTone({
      frequency: 523.25,
      duration: 0.14,
      volume: 0.1,
    });

    if (!firstTonePlayed) {
      /*
       * The new page may not have received
       * user activation yet.
       *
       * Retry after the next interaction.
       */
      pendingSuccessSoundRef.current = true;

      return false;
    }

    /*
     * E5
     */
    await playTone({
      frequency: 659.25,
      startDelay: 0.1,
      duration: 0.14,
      volume: 0.11,
    });

    /*
     * G5
     */
    await playTone({
      frequency: 783.99,
      startDelay: 0.2,
      duration: 0.24,
      volume: 0.13,
    });

    successSoundPlayedRef.current = true;

    pendingSuccessSoundRef.current = false;

    return true;
  }, [playTone]);

  /*
   * -------------------------------------------------------
   * USER INTERACTION / AUDIO UNLOCK
   * -------------------------------------------------------
   *
   * Browsers such as Safari may not permit
   * sound until the user interacts with
   * the application.
   */

  useEffect(() => {
    const handleUserInteraction = async () => {
      const ctx = audioContextRef.current;

      /*
       * Avoid unnecessary work when:
       *
       * - audio is already running
       * - there is no pending sound
       */
      if (
        ctx?.state === "running" &&
        !pendingUpdateSoundRef.current &&
        !pendingSuccessSoundRef.current
      ) {
        return;
      }

      const ready = await resumeAudio();

      if (!ready) {
        return;
      }

      /*
       * Retry update notification.
       */
      if (pendingUpdateSoundRef.current && updateAvailableRef.current) {
        await playUpdateAvailableSound();
      }

      /*
       * Retry success notification.
       */
      if (pendingSuccessSoundRef.current) {
        await playUpdateSuccessSound();
      }
    };

    window.addEventListener("pointerdown", handleUserInteraction, {
      passive: true,
    });

    window.addEventListener("keydown", handleUserInteraction);

    window.addEventListener("touchstart", handleUserInteraction, {
      passive: true,
    });

    return () => {
      window.removeEventListener("pointerdown", handleUserInteraction);

      window.removeEventListener("keydown", handleUserInteraction);

      window.removeEventListener("touchstart", handleUserInteraction);
    };
  }, [resumeAudio, playUpdateAvailableSound, playUpdateSuccessSound]);

  /*
   * -------------------------------------------------------
   * PLAY UPDATE AVAILABLE SOUND
   * -------------------------------------------------------
   */

  useEffect(() => {
    if (!updateAvailable) {
      updateSoundPlayedRef.current = false;

      pendingUpdateSoundRef.current = false;

      return;
    }

    /*
     * Attempt immediately.
     *
     * If autoplay is blocked,
     * playUpdateAvailableSound() will mark
     * it as pending.
     */
    void playUpdateAvailableSound();
  }, [updateAvailable, playUpdateAvailableSound]);

  /*
   * -------------------------------------------------------
   * COMPLETE UPDATE
   * -------------------------------------------------------
   */

  const markUpdateComplete = useCallback(async () => {
    clearStoredUpdateState();

    setIsUpdating(false);
    setUpdateError("");
    setShowSuccess(true);

    /*
     * Reset before trying the new
     * success notification.
     */
    successSoundPlayedRef.current = false;

    pendingSuccessSoundRef.current = true;

    await playUpdateSuccessSound();
  }, [clearStoredUpdateState, playUpdateSuccessSound]);

  /*
   * -------------------------------------------------------
   * DETECT UPDATE COMPLETION WITHOUT RELOAD
   * -------------------------------------------------------
   *
   * Some service-worker implementations can
   * update the app without immediately causing
   * a hard reload.
   */

  useEffect(() => {
    if (isUpdating && !updateAvailable) {
      void markUpdateComplete();
    }
  }, [isUpdating, updateAvailable, markUpdateComplete]);

  /*
   * -------------------------------------------------------
   * DETECT UPDATE AFTER RELOAD
   * -------------------------------------------------------
   *
   * The previous application writes an
   * "update in progress" flag.
   *
   * The newly loaded application checks:
   *
   * 1. Did the user initiate an update?
   * 2. Is the update no longer available?
   *
   * If so, the new version has most likely
   * taken control.
   */

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const updateInProgress = sessionStorage.getItem(UPDATE_IN_PROGRESS_KEY);

    if (updateInProgress !== "true") {
      return;
    }

    const startedAt = Number(sessionStorage.getItem(UPDATE_STARTED_AT_KEY));

    /*
     * Remove stale update state.
     */
    if (
      !Number.isFinite(startedAt) ||
      Date.now() - startedAt > UPDATE_STATE_MAX_AGE
    ) {
      clearStoredUpdateState();

      return;
    }

    /*
     * Give the service-worker hook a moment
     * to determine whether an update is
     * still available.
     */
    const timer = window.setTimeout(() => {
      if (!updateAvailableRef.current) {
        void markUpdateComplete();
      }
    }, 1800);

    return () => {
      window.clearTimeout(timer);
    };
  }, [clearStoredUpdateState, markUpdateComplete]);

  /*
   * -------------------------------------------------------
   * AUTO-HIDE SUCCESS MESSAGE
   * -------------------------------------------------------
   */

  useEffect(() => {
    if (!showSuccess) {
      return;
    }

    const timer = window.setTimeout(() => {
      setShowSuccess(false);

      /*
       * Do not play a delayed success sound
       * much later after the message is gone.
       */
      pendingSuccessSoundRef.current = false;
    }, 4500);

    return () => {
      window.clearTimeout(timer);
    };
  }, [showSuccess]);

  /*
   * -------------------------------------------------------
   * APPLY UPDATE
   * -------------------------------------------------------
   */

  const handleApplyUpdate = useCallback(async () => {
    /*
     * Prevent double-clicks.
     */
    if (isUpdating) {
      return;
    }

    setUpdateError("");
    setIsUpdating(true);

    /*
     * Because this function is triggered
     * directly by a click, this is the best
     * opportunity to unlock audio.
     */
    await resumeAudio();

    /*
     * Mark update as started.
     *
     * Unlike the old implementation, we
     * DON'T claim that the update is
     * completed before applyUpdate() runs.
     */
    sessionStorage.setItem(UPDATE_IN_PROGRESS_KEY, "true");

    sessionStorage.setItem(UPDATE_STARTED_AT_KEY, String(Date.now()));

    try {
      /*
       * Works whether applyUpdate returns
       * a Promise or a regular value.
       *
       * If it reloads immediately, execution
       * naturally stops here and the new app
       * instance handles completion.
       */
      await Promise.resolve(applyUpdate());

      /*
       * If the hook updated the application
       * without reloading, updateAvailable
       * will normally become false and the
       * effect above will mark completion.
       */
      if (!updateAvailableRef.current) {
        await markUpdateComplete();
      }
    } catch (error) {
      console.error("Unable to apply application update:", error);

      clearStoredUpdateState();

      setIsUpdating(false);

      setUpdateError("The update could not be applied. Please try again.");
    }
  }, [
    applyUpdate,
    clearStoredUpdateState,
    isUpdating,
    markUpdateComplete,
    resumeAudio,
  ]);

  /*
   * -------------------------------------------------------
   * CLEANUP AUDIO CONTEXT
   * -------------------------------------------------------
   */

  useEffect(() => {
    return () => {
      const ctx = audioContextRef.current;

      if (ctx && ctx.state !== "closed") {
        ctx.close().catch(() => {});
      }

      audioContextRef.current = null;
    };
  }, []);

  /*
   * -------------------------------------------------------
   * UI
   * -------------------------------------------------------
   */

  return (
    <AnimatePresence mode="wait">
      {/* -----------------------------------
          UPDATE SUCCESS
      ------------------------------------ */}

      {showSuccess ? (
        <motion.div
          key="update-success"
          role="status"
          aria-live="polite"
          initial={
            reduceMotion
              ? false
              : {
                  opacity: 0,
                  y: 24,
                  scale: 0.98,
                }
          }
          animate={{
            opacity: 1,
            y: 0,
            scale: 1,
          }}
          exit={
            reduceMotion
              ? {
                  opacity: 0,
                }
              : {
                  opacity: 0,
                  y: 16,
                  scale: 0.98,
                }
          }
          transition={
            reduceMotion
              ? {
                  duration: 0,
                }
              : {
                  type: "spring",
                  stiffness: 380,
                  damping: 32,
                }
          }
          className="
            fixed inset-x-4 bottom-4 z-[9999]
            sm:inset-x-auto
            sm:right-6
            sm:bottom-6
            sm:max-w-sm
          "
        >
          <div className="relative overflow-hidden bg-white border shadow-2xl rounded-2xl border-white/10 shadow-black/40 backdrop-blur-sm ring-1 ring-black/5 dark:bg-gray-900">
            {/* Accent */}
            <div className="absolute w-32 h-32 rounded-full pointer-events-none -right-10 -top-10 bg-indigo-500/20 blur-2xl" />

            <div className="relative flex items-center gap-3 p-4 ">
              <span className="flex items-center justify-center flex-shrink-0 text-green-600 bg-gray-200 rounded-full h-9 w-9 dark:bg-indigo-500/15 dark:text-indigo-300">
                <CheckCircle2
                  className="h-[18px] w-[18px]"
                  aria-hidden="true"
                />
              </span>

              <div className="min-w-0">
                <p className="text-sm font-medium leading-snug text-black dark:text-white">
                  Application updated successfully
                </p>

                <p
                  className="
                    mt-0.5
                    text-xs
                    leading-snug
                    text-gray-800
                    dark:text-neutral-400
                  "
                >
                  You are now using the latest version.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      ) : updateAvailable ? (
        /*
         * -----------------------------------
         * UPDATE AVAILABLE
         * -----------------------------------
         */

        <motion.div
          key="update-available"
          role="status"
          aria-live="polite"
          aria-busy={isUpdating}
          initial={
            reduceMotion
              ? false
              : {
                  opacity: 0,
                  y: 24,
                  scale: 0.98,
                }
          }
          animate={{
            opacity: 1,
            y: 0,
            scale: 1,
          }}
          exit={
            reduceMotion
              ? {
                  opacity: 0,
                }
              : {
                  opacity: 0,
                  y: 16,
                  scale: 0.98,
                }
          }
          transition={
            reduceMotion
              ? {
                  duration: 0,
                }
              : {
                  type: "spring",
                  stiffness: 380,
                  damping: 32,
                }
          }
          className="
            fixed inset-x-4 bottom-4 z-[9999]
            sm:inset-x-auto
            sm:right-6
            sm:bottom-6
            sm:max-w-sm
          "
        >
          <div className="relative overflow-hidden bg-white border shadow-2xl rounded-2xl border-white/10 shadow-black/40 backdrop-blur-sm ring-1 ring-black/5 dark:bg-gray-900">
            {/* Subtle accent glow */}
            <div className="absolute w-32 h-32 rounded-full pointer-events-none -right-10 -top-10 bg-indigo-500/20 blur-2xl" />

            <div className="relative flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:gap-4">
              {/* Message */}
              <div className="flex items-start gap-3 sm:items-center">
                <span className="flex items-center justify-center flex-shrink-0 text-green-600 bg-gray-200 rounded-full h-9 w-9 dark:bg-indigo-500/15 dark:text-indigo-300">
                  <Sparkles className="h-[18px] w-[18px]" aria-hidden="true" />
                </span>

                <div className="min-w-0">
                  <p className="text-sm font-medium leading-snug text-black dark:text-white">
                    {isUpdating
                      ? "Updating application..."
                      : "A new version is available"}
                  </p>

                  <p
                    className="
                      mt-0.5
                      text-xs
                      leading-snug
                      text-gray-800
                      dark:text-neutral-400
                    "
                  >
                    {isUpdating
                      ? "The latest version is being applied."
                      : "Press Update now to get the latest features and fixes."}
                  </p>

                  {updateError && (
                    <p
                      role="alert"
                      className="
                        mt-1.5
                        text-xs
                        font-medium
                        text-gray-800
                        dark:text-neutral-400
                      "
                    >
                      {updateError}
                    </p>
                  )}
                </div>
              </div>

              {/* Update button */}
              <div className="flex items-center flex-shrink-0 gap-2 sm:ml-auto">
                <motion.button
                  type="button"
                  onClick={handleApplyUpdate}
                  disabled={isUpdating}
                  whileHover={
                    reduceMotion || isUpdating
                      ? undefined
                      : {
                          scale: 1.03,
                        }
                  }
                  whileTap={
                    reduceMotion || isUpdating
                      ? undefined
                      : {
                          scale: 0.97,
                        }
                  }
                  className="
                    inline-flex
                    w-full
                    items-center
                    justify-center
                    gap-1.5
                    rounded-lg
                    bg-green-600
                    px-3.5
                    py-2
                    text-sm
                    font-semibold
                    text-white
                    shadow-sm
                    transition-opacity
                    hover:opacity-90
                    focus:outline-none
                    focus-visible:ring-2
                    focus-visible:ring-indigo-400
                    focus-visible:ring-offset-2
                    disabled:cursor-not-allowed
                    disabled:opacity-70
                    dark:bg-white
                    dark:text-neutral-900
                    sm:w-auto
                  "
                >
                  <Loader
                    className={`
                      h-3.5
                      w-3.5
                      ${
                        isUpdating
                          ? "animate-spin [animation-duration:0.6s] motion-reduce:animate-none"
                          : ""
                      }
                    ${isUpdating ? "" : "hidden"}`}
                    aria-hidden="true"
                  />

                  {isUpdating ? "Updating..." : "Update now"}
                </motion.button>
              </div>
            </div>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

function App() {
  return (
    <div className="flex flex-col w-full min-h-screen overflow-hidden appearance-none bg-slate-100 dark:bg-gray-900 font-roboto">
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
      <UpdateBanner />
      <BrowserRouter>
        <UserContextProvider>
          <DataProvider>
            <Routes>
              {/* Password reset route */}
              <Route path="passwordUpdate" element={<PasswordUpdate />} />

              <Route
                path="/"
                element={
                  <AuthRoute>
                    <UserLogin />
                  </AuthRoute>
                }
              />
              <Route path="passwordreset" element={<PasswordReset />} />

              {/* Protect all routes under 'home' */}
              <Route
                path="home"
                element={
                  <ProtectedRoute>
                    <Index />
                  </ProtectedRoute>
                }
              >
                <Route path="home" index element={<HomePage />} />
                <Route path="vano" element={<VANOHome />} />
                <Route path="userProfile" element={<UserProfile />} />
                <Route path="Offline_farmer" element={<OfflineFarmers />} />
                <Route path="farmer" element={<FarmerHome />} />
                <Route path="packages" element={<HomePackage />} />
                <Route path="statistics" element={<Statistics />} />
                <Route path="objective" element={<Objective />} />
                <Route path="payments" element={<PaymentsUpdater />} />
                <Route path="farmerSettings" element={<FarmerSettings />} />
              </Route>
            </Routes>
          </DataProvider>
        </UserContextProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
